import React from "react";
import { Flex, Tag, } from "antd";
import { Handle, Position } from "reactflow";
import { actionCodeToTitle, getActionIconAndClass } from "../utils/actionCodeMapping";
import { NOTIFY_USER_MESSAGE_FALLBACK } from "../utils/notifyUserStaticMessage";
import { useWorkflowStore } from "../store";

export interface ActionNodeData {
    actionType?: string;
    actionCode?: string;
    title?: string;
    isPlaceholder?: boolean;
    message?: string;
    onceOnly?: boolean;
    responseType?: string;
    templateId?: string;
    templateName?: string;
    templateIntegId?: string;
    hasMedia?: boolean;
    mediaFileList?: unknown[];
    components?: unknown;
    fieldNameAndValueMap?: Record<string, unknown>;

    recordOwner?: boolean;

    accountOwner?: boolean;

    includeAdditionalEmails?: boolean;

    additionalEmails?: string[];

    logRecordAsThread?: string;
    logRecordsFor?: string;

    delayDays?: number;
    delayHours?: number;
    delayMinutes?: number;

    createLead?: string;
}

const ACTION_META: Record<string, { defaultTitle: string }> = {
    AutomatedResponse: { defaultTitle: "Automated Response" },
    NotifyUser: { defaultTitle: "Notify User" },
    CreateLead: { defaultTitle: "Create Lead" },
    LogActivity: { defaultTitle: "Log Activity" },
    AddNote: { defaultTitle: "Add Note" },
    Delay: { defaultTitle: "Delay" },
};

const ACTION_CODE_TO_META_KEY: Record<string, string> = {
    "Action.Message.AutomatedResponse": "AutomatedResponse",
    "Action.Notify.NotifyUser": "NotifyUser",
    "Action.Crm.AddNote": "AddNote",
    "Action.Crm.LogActivity": "LogActivity",
    "Action.Crm.CreateLead": "CreateLead",
    "Action.FlowControl.Delay": "Delay",
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="d-flex align-items-start gap-2">
        <span className="hs-fs-12 hs-fw-400 hs-color-BlackRussian text-nowrap flex-shrink-0">{label}</span>
        <Flex gap="small" align="start" className="hs-fs-12 hs-fw-400 hs-color-BlackRussian flex-wrap min-w-0 flex-grow-1">
            {children}
        </Flex>
    </div>
);

const Badge: React.FC<{ children: React.ReactNode; className?: string; title?: string }> = ({ children, className, title }) => (
    <span title={title} className={`hs-fs-12 hs-text-truncate mw-100 rounded-1 hs-color-BlackRussian ${className ?? ""}`.trim()}>{children}</span>
);

function hasTemplateComponents(components: unknown): boolean {
    if (components === undefined || components === null) return false;
    if (Array.isArray(components)) return components.length > 0;
    if (typeof components === "object") return Object.keys(components as Record<string, unknown>).length > 0;
    return false;
}

function messageBodyLooksLikeUrl(message: string): boolean {
    return /^https?:\/\//i.test(message.trim());
}

const DEFAULT_NODE_ICON = { icon: <div className="automationIconsSprite select-icon" /> };

function getNodeIcon(data: ActionNodeData) {
    if (!data.actionCode && !data.actionType) return DEFAULT_NODE_ICON;
    return getActionIconAndClass(data.actionCode ?? data.actionType);
}

function getActionFormType(data: ActionNodeData): string {
    if (data.actionCode && ACTION_CODE_TO_META_KEY[data.actionCode]) {
        return ACTION_CODE_TO_META_KEY[data.actionCode];
    }
    return data.actionType ?? "";
}

const renderActionDetails = (
    data: ActionNodeData,
    applyOncePerContact?: boolean | null,
    notifyUserStaticMessage?: string
) => {
    const formType = getActionFormType(data);
    switch (formType) {
        case "AutomatedResponse": {
            const usesTemplate = Boolean(
                (data.templateId && data.templateId.trim() !== "") ||
                    (data.templateName && data.templateName.trim() !== "") ||
                    hasTemplateComponents(data.components)
            );
            const templateTitle =
                data.templateName?.trim() || (usesTemplate ? "Selected template" : "");
            const msg = data.message?.trim() ?? "";
            const bodyIsUrl = msg.length > 0 && messageBodyLooksLikeUrl(msg);
            const showPlainTextMessage =
                !usesTemplate && msg.length > 0 && !(data.hasMedia && bodyIsUrl);
            const showMediaOnly =
                !usesTemplate && data.hasMedia && (!msg || bodyIsUrl);
            return (
                <>
                    {usesTemplate && templateTitle && (
                        <DetailRow label="Template:">
                            <Badge title={templateTitle}>{templateTitle}</Badge>
                        </DetailRow>
                    )}
                    {data.responseType && (
                        <DetailRow label="Response:">
                            <Badge title={data.responseType}>{data.responseType}</Badge>
                        </DetailRow>
                    )}
                    {showPlainTextMessage && (
                        <DetailRow label="Message:">
                            <span className="trigger-rule-node-detail-value-message overflow-hidden text-break flex-fill font-italic hs-color-BlackRussian">
                                &ldquo;{msg}&rdquo;
                            </span>
                        </DetailRow>
                    )}
                    {showMediaOnly && (
                        <DetailRow label="Message:">
                            <Badge>Media files attached</Badge>
                        </DetailRow>
                    )}
                    {(applyOncePerContact ?? data.onceOnly) && (
                        <DetailRow label="Apply once per contact:">
                            <Badge>Yes</Badge>
                        </DetailRow>
                    )}
                </>
            );
        }
        case "NotifyUser":
            const recipients: string[] = [];
            if (data.recordOwner) recipients.push("Record Owner");
            if (data.accountOwner) recipients.push("Account Owner");
            if (data.includeAdditionalEmails && data.additionalEmails?.length) {
                recipients.push(...data.additionalEmails);
            }
            return (
                <>
                    {recipients.length > 0 && (
                        <DetailRow label="Send to:">
                            <div className="d-flex flex-wrap gap-1 mw-100">
                                {recipients.map((r) => (
                                    <Tag key={r} title={r} className="hs-fs-12 hs-fw-400 hs-color-dark-grey hs-bg-pale-blue-1 hs-border-md-grey rounded-2 m-0 mw-100 hs-text-truncate">{r}</Tag>
                                ))}
                            </div>
                        </DetailRow>
                    )}
                    <DetailRow label="Message:">
                        <span className="trigger-rule-node-detail-value-message overflow-hidden text-break flex-fill font-italic hs-color-BlackRussian">
                            &ldquo;
                            {notifyUserStaticMessage?.trim()
                                ? notifyUserStaticMessage
                                : NOTIFY_USER_MESSAGE_FALLBACK}
                            &rdquo;
                        </span>
                    </DetailRow>
                </>
            );
        case "AddNote":
            return (
                <>
                    {data.logRecordsFor && (
                        <DetailRow label="Log to:">
                            <span className="hs-fs-12 hs-fw-400 hs-color-BlackRussian">
                                {data.logRecordsFor === "recent" ? "Recent record" : "All matching records"}
                            </span>
                        </DetailRow>
                    )}
                </>
            );
        case "LogActivity":
            return (
                <>
                    {data.logRecordAsThread && (
                        <DetailRow label="Merge:">
                            <span className="hs-fs-12 hs-fw-400 hs-color-BlackRussian">
                                {data.logRecordAsThread === "true" ? "Yes" : "No"}
                            </span>
                        </DetailRow>
                    )}
                    {data.logRecordsFor && (
                        <DetailRow label="Log to:">
                            <span className="hs-fs-12 hs-fw-400 hs-color-BlackRussian">
                                {data.logRecordsFor === "recent" ? "Recent record" : "All matching records"}
                            </span>
                        </DetailRow>
                    )}
                </>
            );
        case "CreateLead":
            return (
                <DetailRow label="Auto-create lead:">
                    <span className="hs-fs-12 hs-fw-400 hs-color-BlackRussian">
                        {data.createLead === "false" ? "No" : "Yes"}
                    </span>
                </DetailRow>
            );
        case "Delay": {
            const d = data.delayDays ?? 0;
            const h = data.delayHours ?? 0;
            const m = data.delayMinutes ?? 0;
            const parts: string[] = [];
            if (d > 0) parts.push(`${d}d`);
            if (h > 0) parts.push(`${h}h`);
            if (m > 0) parts.push(`${m}m`);
            const label = parts.length > 0 ? parts.join(" ") : "—";
            return (
                <DetailRow label="Wait for:">
                    <Badge>{label}</Badge>
                </DetailRow>
            );
        }
        default:
            return null;
    }
};

function getActionMeta(data: ActionNodeData) {
    const key = data.actionCode
        ? ACTION_CODE_TO_META_KEY[data.actionCode] ?? data.actionType
        : data.actionType;
    return key ? ACTION_META[key] : null;
}

const ActionNodeComponent: React.FC<{ data: ActionNodeData; selected?: boolean }> = ({ data, selected }) => {
    const hasTypeSelected = !!(data.actionCode || data.actionType);
    const isPlaceholder = data.isPlaceholder ?? !hasTypeSelected;
    const meta = getActionMeta(data);
    const applyOncePerContact = useWorkflowStore((s) => s.applyOncePerContact);
    const notifyUserStaticMessage = useWorkflowStore((s) => s.notifyUserStaticMessage);

    const displayTitle = isPlaceholder
        ? "Select an Action..."
        : (data.title ?? meta?.defaultTitle ?? actionCodeToTitle(data.actionCode) ?? "Action");

    return (
        <div
            className={`hs-w-350-px position-relative hs-border-grey rounded-4 p-3 ${isPlaceholder ? "trigger-rule-node-placeholder hs-border-color-placeholder bg-transparent" : "hs-bg-plain-white"} ${selected ? "hs-border-violet" : ""}`}
            role="button"
            tabIndex={0}
            aria-label={`Action: ${displayTitle}. ${selected ? "Selected" : "Click to select"}`}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    (e.currentTarget as HTMLElement).click();
                }
            }}
        >
            <Handle type="target" position={Position.Top} id="target" className="opacity-0"/>
            <Handle type="source" position={Position.Bottom} id="source" className="opacity-0"/>
            <span className={`trigger-rule-node-tag hs-fs-11 hs-fw-600 rounded-3 border-1 hs-border-violet py-1 px-3 ${isPlaceholder ? "tag-fill" : "hs-color-violet hs-bg-plain-white"}`}>{isPlaceholder ? "+ NEW STEP" : "Action"}</span>

            {isPlaceholder ? (
                <div className="d-flex align-items-center gap-2">
                    <div className="d-flex align-items-center justify-content-center">
                        {getNodeIcon(data).icon}
                    </div>
                    <div className="d-flex flex-column gap-1 flex-grow-1 min-w-0">
                        <span className="hs-fw-600 hs-color-BlackRussian hs-fs-12">Select an Action...</span>
                        <span className="hs-fs-12 hs-color-medium-dark">Choose from the panel on the right</span>
                    </div>
                </div>
            ) : (
                <>
                    <div className=" d-flex align-items-center gap-2">
                        <div className=" d-flex align-items-center justify-content-center">
                            {getNodeIcon(data).icon}
                        </div>
                        <span className="hs-fw-600 hs-color-BlackRussian hs-fs-12">
                            {data.title ?? meta?.defaultTitle ?? actionCodeToTitle(data.actionCode) ?? "Action"}
                        </span>
                    </div>
                    <Flex vertical gap="middle" justify="space-between" className="pt-3">
                        {renderActionDetails(data, applyOncePerContact, notifyUserStaticMessage)}
                    </Flex>
                </>
            )}
        </div>
    );
};

export const ActionNode = React.memo(ActionNodeComponent);
